import { AppRoute } from '@/constants/shared';
import { useAuth } from '@/hooks/useAuth';
import { useCurrentUserData } from '@/hooks/useCurrentUserData';
import { useDailySpinOnchain } from '@/hooks/useDailySpinOnchain';
import { usePlumeStaking } from '@/hooks/usePlumeStaking';
import { useNucleusRoycoPositionsData } from '@/hooks/useRoycoPositionsData';
import { useWalletBalanceInfo } from '@/hooks/useWalletBalancesInfo';
import { Box, FormatNumber, Heading, HStack, SimpleGrid, Skeleton, Stack, Text, VStack } from '@chakra-ui/react';
import { ReactNode } from 'react';
import { FiArrowRight } from 'react-icons/fi';
import { SpinningButton } from './app-header/daily-spin-popover';
import { HoverCardWithLink } from './hover-card-with-link';
import { RoundedBadge } from './rounded-badge';
import { TooltipIcon } from './tooltip-icon';

const Stat = ({ label, value, tooltip }: { label: string; value: ReactNode; tooltip?: string }) => {
  return (
    <Box
      display="flex"
      xlDown={{
        mt: 2,
      }}
    >
      <Stack
        flexDirection={['row', 'row', 'row', 'row', 'column']}
        gap={3}
        w="full"
        // TODO: figure out how to stop overlapping on small windows
        alignItems={['center', 'center', 'center', 'center', 'flex-start']}
        justifyContent="space-between"
      >
        <HStack gap={1}>
          <Text fontSize="sm" fontWeight="500" color="#85907F" textAlign="left" whiteSpace="nowrap">
            {label}
          </Text>
          {tooltip && <TooltipIcon content={tooltip} color="#85907F" />}
        </HStack>
        {value}
      </Stack>
    </Box>
  );
};

export const UserProfileCard = () => {
  const { currentUserData, isLoading: isLoadingCurrentUserData } = useCurrentUserData();
  const { dailySpinStreak, hasSpunToday, isLoadingDailySpinStats } = useDailySpinOnchain();
  const { address } = useAuth();
  const { stats } = currentUserData ?? {};

  const { totalXp } = stats ?? {};
  const { plumeStakingRewardAmountTotalEquivInPlume, PLUME_USD_PRICE, isLoadingPlumeStaking } = usePlumeStaking();
  const { totalUSDValueOfWallet, isLoading: isLoadingBalance } = useWalletBalanceInfo(address ?? '');

  const { totalPlumeEarned, isLoadingRoycoPositionsData } = useNucleusRoycoPositionsData();

  const plumeEarnings =
    (stats?.plumeStakingClaimedAmount?.plume ?? 0) + // amount claimed from contract in plume
    (stats?.plumeStakingClaimedAmount?.usdc ?? 0) / PLUME_USD_PRICE + // amount claimed from contract in usdc
    (plumeStakingRewardAmountTotalEquivInPlume ?? 0) + // amount available to claim from contract in plume+usdc converted to plume equivalent
    Number(totalPlumeEarned); // amount earned from optimizer

  return (
    <HoverCardWithLink href={AppRoute.MyProfile} cardRootStyles={{ borderRadius: '2xl' }}>
      <VStack gap={0} alignItems="start" h="full" bgColor="#EAF0E9" p={8}>
        <HStack w="full" justifyContent="space-between">
          <Text fontSize="sm" fontWeight="500" color="#85907F" alignSelf="flex-start">
            Your Stats
          </Text>
          <RoundedBadge
            h={11}
            px={4}
            bgColor="bg"
            _groupHover={{
              bgColor: 'bg.subtle',
            }}
            alignSelf="flex-end"
          >
            My Profile
            <FiArrowRight />
          </RoundedBadge>
        </HStack>
        {isLoadingBalance ? (
          <Skeleton w="100px" h="30px" />
        ) : (
          <Heading fontSize={['36px', '36px', '36px', '40px']} fontWeight="bold" as="div" color="#4E5848">
            <FormatNumber value={totalUSDValueOfWallet} style="currency" currency="USD" />
          </Heading>
        )}

        <SimpleGrid columns={[1, 1, 1, 1, 3]} w="full" mt={8}>
          <Stat
            label="Plume Points"
            tooltip="This is your total earnings of Plume Points. The more PP you earn, the higher your share of Plume’s Airdrop Season 2. Earn more PP by participating in Plume’s RWAfi ecosystem."
            value={
              isLoadingCurrentUserData ? (
                <Skeleton w="100px" h={['20px', '20px', '30px']} />
              ) : (
                <Heading
                  fontSize={['lg', 'lg', 'lg', 'lg', '3xl']}
                  fontWeight="500"
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  <FormatNumber value={totalXp || 0} style="decimal" maximumFractionDigits={0} />

                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect width="24" height="24" rx="12" fill="#00D594" />
                    <path
                      d="M14.5714 9.53233C14.5241 9.50758 14.4904 9.47238 14.4675 9.42838L12 3.75L9.53233 9.42857C9.5096 9.47238 9.47568 9.50758 9.42838 9.53252L3.75 12L9.42857 14.4677C9.4733 14.4866 9.50868 14.5201 9.53252 14.5716L12 20.25L14.4677 14.5714C14.4915 14.5201 14.5269 14.4866 14.5716 14.4675L20.25 12L14.5714 9.53233ZM12 13.6944C11.0643 13.6944 10.3056 12.9357 10.3056 12C10.3056 11.0643 11.0641 10.3056 12 10.3056C12.9359 10.3056 13.6944 11.0643 13.6944 12C13.6944 12.9357 12.9357 13.6944 12 13.6944Z"
                      fill="white"
                    />
                  </svg>
                </Heading>
              )
            }
          />
          <Stat
            label="PLUME Earnings"
            tooltip="This is your total PLUME earnings, from the Yield Optimizer and Royco campaigns, as well as from Plume Staking. You can earn more PLUME by depositing more TVL into these products."
            value={
              isLoadingPlumeStaking || isLoadingRoycoPositionsData ? (
                <Skeleton w="100px" h={['20px', '20px', '30px']} />
              ) : (
                <Heading
                  fontSize={['lg', 'lg', 'lg', 'lg', '3xl']}
                  fontWeight="500"
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  <FormatNumber value={plumeEarnings || 0} style="decimal" maximumFractionDigits={0} />

                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect width="24" height="24" rx="12" fill="#FF3D00" />
                    <path d="M14.5743 9.81636V14.1235L12.8835 12.4186V8.11151L14.5743 9.81636Z" fill="white" />
                    <path d="M9.86052 14.5717H14.13L12.44 12.866H8.17059L9.86052 14.5717Z" fill="white" />
                    <path
                      d="M16.9774 17.0111V12.2406L15.2866 10.5358V15.3062L10.5737 15.2903L12.2636 16.996L16.9765 17.0119"
                      fill="white"
                    />
                    <path
                      d="M6 6.00088L6 10.7713L7.6908 12.4761L7.6908 7.70572L12.4037 7.72164L10.7138 6.01592L6.0009 6"
                      fill="white"
                    />
                    <path
                      d="M17.4244 17.9171L16.4652 16.9493L16.9311 16.4793L17.8904 17.447L18 18L17.4244 17.9171Z"
                      fill="white"
                    />
                  </svg>
                </Heading>
              )
            }
          />
          <Stat
            label="Spin Streak"
            tooltip="This is your daily spin streak. Spin everyday and maintain your streak to access more rewards."
            value={
              isLoadingDailySpinStats ? (
                <Skeleton w="100px" h={['38px']} />
              ) : (
                <Heading
                  fontSize={['lg', 'lg', 'lg', 'lg', '3xl']}
                  fontWeight="500"
                  display="flex"
                  alignItems="center"
                  gap={2}
                  as="div"
                >
                  {dailySpinStreak}d{!hasSpunToday ? <SpinningButton label="Spin" h={8} ml={2} /> : null}
                </Heading>
              )
            }
          />
        </SimpleGrid>
      </VStack>
    </HoverCardWithLink>
  );
};
